/**
 * PostHog client wrapper — typed product-event registry + safe init.
 *
 * Why a separate file (not folded into `@/lib/analytics`):
 *   - GA4 stays a thin window.gtag pass-through (loaded via <Script> in layout).
 *   - PostHog is a stateful client (init + identify + capture), so it gets its
 *     own module with `getPostHog()` caching + a `void` no-op when the key is
 *     missing. Existing `trackEvent()` in `@/lib/analytics` fans out to BOTH
 *     surfaces, so call sites do not need to know which provider is active.
 *
 * Init behavior:
 *   - `NEXT_PUBLIC_POSTHOG_KEY` missing → silent no-op. No init, no console
 *     log, no Sentry — pre-launch foundation file MUST be safe to ship even
 *     when ops hasn't provisioned the project yet.
 *   - Browser host defaults to Foundry's first-party proxy (`/_forge`) which
 *     rewrites to the US PostHog ingest/assets cluster. The Foundry PostHog
 *     project (418201) lives in the US region (see memory note
 *     `credentials_posthog.md`). Do NOT switch the upstream to EU for this
 *     project — events will 404 / silently disappear.
 *   - Privacy defaults: `respect_dnt: true`; session recording is opt-in via
 *     `NEXT_PUBLIC_POSTHOG_SESSION_RECORDING_ENABLED`. When enabled, text,
 *     input values, element attributes, and network payloads stay masked/off.
 *     Autocapture OFF — we want a typed event registry, not a firehose of
 *     dom-blob events.
 *     `capture_pageview` / `capture_pageleave` also OFF: GA4 handles pageviews,
 *     PostHog page-level events fan out via `trackEvent()`.
 *
 * Anti-pattern note: the event-name registry below is a `const` at module
 * top-level used as TYPE source (the union literal), not as a control-flow
 * value. No `for`/`while`/`if` consumes it as a runtime number. This is the
 * "OK at top-level: pure data — schemas, prompt strings, type enums" case
 * documented in .claude/rules/anti-patterns.md.
 */

import type { PostHog } from "posthog-js";
import { mirrorAnalyticsEvent } from "@/lib/analytics/fanout";

/** Event registry — single source of truth for the analytics taxonomy. */
export const EXPORT_EVENTS = [
  "export.brand_pdf",
  "export.brand_tokens",
  "export.brand_share",
  "export.plan_pdf",
  "export.niche_pdf",
] as const;

export type ExportEventName = (typeof EXPORT_EVENTS)[number];

/** Per-event property shape — minimum required for funnel "attempt → success". */
export interface ExportEventProps {
  /** Artifact ID (niche id, brand kit id, plan id) — denormalised pivot key. */
  artifact_id: string;
  /** User tier at the moment of click (free / starter / pro / etc.). */
  tier: string;
  /** Optional render format hint — pdf / png / json / csv. */
  format?: string;
  /** Optional sub-variant (e.g. share-card type: "founder" | "release"). */
  variant?: string;
}

interface PostHogConfig {
  apiKey: string;
  apiHost: string;
  uiHost: string;
  sessionRecordingEnabled: boolean;
  sessionRecordingSampleRate: number;
}

type EventParams = Record<string, string | number | boolean | undefined>;
type PostHogCaptureOptions = Parameters<PostHog["capture"]>[2];

interface ClientCaptureOptions {
  mirror?: boolean;
  posthog?: PostHogCaptureOptions;
}

function parseBooleanEnv(value: string | undefined): boolean {
  return value === "1" || value === "true";
}

function parseSampleRateEnv(value: string | undefined): number {
  if (!value) return 1;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(0, Math.min(1, parsed));
}

function readConfig(): PostHogConfig | null {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!apiKey) return null;
  // PostHog project 418201 is in US region. Browser traffic goes through our
  // first-party proxy by default; Next rewrites it to us.i.posthog.com.
  // See memory `credentials_posthog.md` + `docs/analytics/analytics-implementation.md` §7.4.
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "/_forge";
  const sessionRecordingEnabled = parseBooleanEnv(
    process.env.NEXT_PUBLIC_POSTHOG_SESSION_RECORDING_ENABLED,
  );
  const sessionRecordingSampleRate = parseSampleRateEnv(
    process.env.NEXT_PUBLIC_POSTHOG_SESSION_RECORDING_SAMPLE_RATE,
  );
  return {
    apiKey,
    apiHost,
    uiHost: "https://us.posthog.com",
    sessionRecordingEnabled,
    sessionRecordingSampleRate,
  };
}

let cachedClient: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;

/**
 * Lazily init the PostHog browser client and cache the singleton.
 * Idempotent: safe to call from multiple deferred-bootstrap entry points
 * (interaction-handler + timeout) without spawning duplicate /e/ requests.
 *
 * Returns null when the key is missing OR before init has run.
 * Returns the singleton when init has completed at least once.
 */
export async function ensurePostHog(): Promise<PostHog | null> {
  if (typeof window === "undefined") return null;
  if (cachedClient) return cachedClient;
  if (initPromise) return initPromise;
  const cfg = readConfig();
  if (!cfg) return null;

  initPromise = (async () => {
    const mod = await import("posthog-js");
    const client = mod.default;
    client.init(cfg.apiKey, {
      api_host: cfg.apiHost,
      // `api_host` is a reverse proxy; keep PostHog UI deep links pointed at
      // the real app host.
      ui_host: cfg.uiHost,
      // GA4 owns pageviews; PostHog page-level events go through the typed
      // registry via `trackEvent()`. Capturing here would double-count.
      capture_pageview: false,
      capture_pageleave: false,
      // Off — we want the typed registry to be the only event surface so
      // funnels stay clean. Avoids ghost `$autocapture click` events that
      // would duplicate explicit `*.start` / `*.complete` captures and burn
      // PostHog quota.
      autocapture: false,
      rageclick: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_performance: false,
      capture_exceptions: false,
      // Privacy defaults: respect browser DNT. Session replay is controlled by
      // env and stays heavily masked if enabled.
      respect_dnt: true,
      disable_session_recording: !cfg.sessionRecordingEnabled,
      disable_surveys: true,
      disable_surveys_automatic_display: true,
      disable_conversations: true,
      disable_product_tours: true,
      // Replay is lazy-loaded by PostHog. Keep this on only when recordings are
      // disabled; when enabled, external chunks are served through /_forge/static.
      disable_external_dependency_loading: !cfg.sessionRecordingEnabled,
      mask_all_text: cfg.sessionRecordingEnabled,
      mask_all_element_attributes: cfg.sessionRecordingEnabled,
      session_recording: {
        sampleRate: cfg.sessionRecordingSampleRate,
        maskAllInputs: true,
        maskTextSelector: "*",
        blockSelector:
          "[data-ph-no-capture], [data-no-recording], [data-sensitive]",
        recordHeaders: false,
        recordBody: false,
      },
      // Keep persistence in localStorage+cookie so anon → identified bridge
      // works the same as our session cookie pattern (critical for distinct_id
      // stability cross-tab).
      persistence: "localStorage+cookie",
    });
    cachedClient = client;
    return client;
  })().catch((err) => {
    initPromise = null;
    // No Sentry import here — this file is client-only and we'd rather not
    // chain another lazy dep just to log a missed analytics init. The site
    // works without PostHog; surfacing this would only spam the dashboard.
    // intentional: silent — PostHog is optional product-analytics; init failure must not break the app
    void err;
    return null;
  });

  try {
    return await initPromise;
  } catch (err) {
    void err;
    return null;
  }
}

/**
 * Type-safe export event helper. Forwards to PostHog when initialised, and
 * mirrors to GA4 + Clarity so export intent is visible in launch funnels.
 */
export function trackExport<T extends ExportEventName>(
  name: T,
  props: ExportEventProps,
): void {
  if (typeof window === "undefined") return;
  mirrorAnalyticsEvent(name, props);
  // Synchronous path: if the client is already cached, capture immediately.
  if (cachedClient) {
    try {
      cachedClient.capture(name, props);
    } catch (err) {
      // intentional: analytics capture must never throw into UI handlers
      void err;
    }
    return;
  }
  // Otherwise kick off init and capture once it lands. Awaiting in the
  // background is OK — the event is queued in JS not in PostHog's batcher,
  // so we can't lose it on a fast page-unload click. For unload-resilient
  // captures we'd need `posthog.capture(name, props, {transport: "sendBeacon"})`
  // — out of scope for the foundation install.
  void ensurePostHog().then((client) => {
    if (!client) return;
    try {
      client.capture(name, props);
    } catch (err) {
      // intentional: see above
      void err;
    }
  });
}

/**
 * Pass-through capture for non-typed events. Used by the GA4 fan-out in
 * `@/lib/analytics::trackEvent` so the loose GA event names also land in
 * PostHog under their original string. Internal — prefer `trackExport`
 * for new typed events.
 */
export function capturePostHog(
  name: string,
  props?: EventParams,
  options?: ClientCaptureOptions,
): void {
  if (typeof window === "undefined") return;
  if (options?.mirror !== false) mirrorAnalyticsEvent(name, props);
  if (cachedClient) {
    try {
      cachedClient.capture(name, props, options?.posthog);
    } catch (err) {
      // intentional: analytics capture must never throw into UI handlers
      void err;
    }
    return;
  }
  void ensurePostHog().then((client) => {
    if (!client) return;
    try {
      client.capture(name, props, options?.posthog);
    } catch (err) {
      // intentional: see above
      void err;
    }
  });
}
